import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import {
  BREAK_GLASS_CODE,
  getLoggedInUserDetails,
  sendBreakGlassLog,
} from 'utils/breakGlass';

const labelMaskSx = {
  '& .MuiInputLabel-root': {
    backgroundColor: '#fff',
    px: 0.5,
  },
};

const DEFAULT_DESCRIPTION =
  'Enter the employee name and break-glass code to authorize this action. This event will be recorded and sent to management.';

const BreakGlassDialog = ({
  open,
  onClose,
  onAuthorized,
  page,
  actionSummary,
  title = 'Break-Glass Authorization',
  description = DEFAULT_DESCRIPTION,
  cancelLabel = 'Cancel',
  submitLabel = 'Unlock',
}) => {
  const loggedInUser = useSelector(({ user }) => user.data);
  const defaultEmployeeName = getLoggedInUserDetails(loggedInUser).firstName || '';

  const [employeeName, setEmployeeName] = useState(defaultEmployeeName);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setEmployeeName((current) => current || defaultEmployeeName);
    }
  }, [open, defaultEmployeeName]);

  const resetFields = () => {
    setEmployeeName(defaultEmployeeName);
    setPassword('');
  };

  const handleClose = () => {
    resetFields();
    onClose?.();
  };

  const handleSubmit = () => {
    const trimmedEmployeeName = employeeName.trim();

    if (!trimmedEmployeeName) {
      toast.error('Please enter the employee name.');
      return;
    }

    const isCorrectPassword = password === BREAK_GLASS_CODE;

    sendBreakGlassLog(
      {
        eventType: 'break-glass-attempt',
        employeeName: trimmedEmployeeName,
        actionSummary: actionSummary || 'Unknown action',
        passwordStatus: isCorrectPassword ? 'correct' : 'wrong',
        loggedInUser: getLoggedInUserDetails(loggedInUser),
      },
      page
    );

    if (!isCorrectPassword) {
      toast.error('Incorrect break-glass code.');
      setPassword('');
      return;
    }

    resetFields();
    toast.success('Break-glass authorization accepted for this action.');
    onAuthorized?.(trimmedEmployeeName);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {actionSummary ? (
          <DialogContentText sx={{ mb: 1 }}>{actionSummary}</DialogContentText>
        ) : null}
        <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Employee Name"
            variant="outlined"
            value={employeeName || ''}
            onChange={(e) => setEmployeeName(e.target.value)}
            fullWidth
            autoFocus
            InputLabelProps={{ shrink: true }}
            sx={labelMaskSx}
          />
          <TextField
            label="Break-Glass Code"
            variant="outlined"
            type="password"
            value={password || ''}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={labelMaskSx}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{cancelLabel}</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakGlassDialog;
